import React, {useEffect, useState, useRef} from "react";
import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    TextField,
    Autocomplete,
} from "@mui/material";
import {GuardianSlider} from "../components/GuardianSlider/GuardianSlider";
import {IncomingMessageBox} from "../components/IncomingMessageBox/IncomingMessageBox";
import {InteractionInterfaceBox} from "../components/InteractionInterfaceBox/InteractionInterfaceBox";
import {HistoryItem} from "../types/history";
import {appendHistoryText, loadHistory} from "../Helpers/Helpers";
import {useNavigate} from "react-router-dom";
import {startTutorialFlow} from "../components/Tutorials/TutorialOverlays/tutorialFlows1";
import CommonDialog from "../components/Shared/CommonDialog/CommonDialog";
import styles from "../Styles/GuardianComponent.module.scss";
import {MobileBottomPanel} from "../components/MobileBottomPanel/MobileBottomPanel";
import {startTutorialFlowMobile} from "../components/Tutorials/TutorialOverlays/tutorialFlowsMobile";
// import { MobileBottomPanel } from "../components/MobileBottomPanel/MobileBottomPanel";
const MOBILE_BREAKPOINT = 900; // px — matches plan spec


export const GuardianComponent = () => {
    const [selectedGuardian, setSelectedGuardian] = useState<any | null>(null);
    const [interactionHistory, setInteractionHistory] = useState<HistoryItem[]>([]);

    const centerActionBoxRef = useRef<HTMLDivElement>(null);
    const selectedGuardianWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateHeights = () => {
            // Use requestAnimationFrame to ensure the DOM has updated before measuring
            requestAnimationFrame(() => {
                if (centerActionBoxRef.current) {
                    document.documentElement.style.setProperty(
                        "--centerActionBoxHeight",
                        `${centerActionBoxRef.current.offsetHeight}px`
                    );
                }
                if (selectedGuardianWrapperRef.current) {
                    document.documentElement.style.setProperty(
                        "--selectedGuardianWrapperHeight",
                        `${selectedGuardianWrapperRef.current.offsetHeight}px`
                    );
                } else {
                    document.documentElement.style.setProperty(
                        "--selectedGuardianWrapperHeight",
                        "0px"
                    );
                }
            });
        };

        // Standard resize listener
        window.addEventListener("resize", updateHeights);

        // Specific orientation change listener for mobile
        if (typeof window.screen.orientation !== "undefined") {
            window.screen.orientation.addEventListener("change", updateHeights);
        } else {
            // Fallback for older browsers
            window.addEventListener("orientationchange", updateHeights);
        }

        // Initial measurement
        updateHeights();

        return () => {
            window.removeEventListener("resize", updateHeights);
            if (typeof window.screen.orientation !== "undefined") {
                window.screen.orientation.removeEventListener("change", updateHeights);
            }
            window.removeEventListener("orientationchange", updateHeights);
        };
    }, [selectedGuardian]);

    const [showStartIntroductionDialog, setShowStartIntroductionDialog] = useState(false);
    const [showChangeLanguagePopup, setShowChangeLanguagePopup] = useState(false);
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState<boolean>(
        () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
    );
    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
        console.log(mediaQuery);
        const handleChange = (e: MediaQueryListEvent) => {
            setIsMobile(e.matches);
        };

        // Modern browsers
        mediaQuery.addEventListener("change", handleChange);

        return () => {
            mediaQuery.removeEventListener("change", handleChange);
        };
    }, []);

    const languages = [
        {code: "en", label: "English"},
        {code: "ar", label: "Arabic"},
        {code: "id", label: "Bahasa Indonesia"},
        {code: "ms", label: "Bahasa Melayu"},
        {code: "zh-CN", label: "Chinese - Simplified"},
        {code: "zh-TW", label: "Chinese - Traditional"},
        {code: "fr", label: "French"},
        {code: "de", label: "German"},
        {code: "hi", label: "Hindi"},
        {code: "it", label: "Italian"},
        {code: "ja", label: "Japanese"},
        {code: "ko", label: "Korean"},
        {code: "pt", label: "Portuguese - Brazil"},
        {code: "pt-PT", label: "Portuguese - Portugal"},
        {code: "ru", label: "Russian"},
        {code: "es", label: "Spanish"},
        {code: "sw", label: "Swahili"},
        {code: "th", label: "Thai"},
        {code: "uk", label: "Ukrainian"},
        {code: "vi", label: "Vietnamese"},
    ];

    useEffect(() => {
        setInteractionHistory(loadHistory());
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem("selectedGuardian");
        if (stored) setSelectedGuardian(JSON.parse(stored));

        const historyStored = localStorage.getItem("interactionHistory");
        if (historyStored) setInteractionHistory(JSON.parse(historyStored));
    }, []);


    const flows = [
        {startFromFlowName: "introFlow", label: "Start Introduction", tutorialStep: 0},
        {
            startFromFlowName: "scamIdentifyFlow1",
            label: "Jump to: Top Menu Bar Functions, Scam Identification",
            tutorialStep: 0,
        },
        {
            startFromFlowName: "scamEducationFlow",
            label: "Jump to: Scam Education, Scam Defences",
            tutorialStep: 0,
        },
        {
            startFromFlowName: "communityFlow",
            label: "Jump to: Contribution & Community, Archives",
            tutorialStep: 0,
        },
    ];

    const handleSelectGuardian = (guardian: any | null) => {
        const stored = localStorage.getItem("selectedGuardian");
        if (!stored || JSON.parse(stored).name !== guardian.name) {
            localStorage.setItem("selectedGuardian", JSON.stringify(guardian));
            setSelectedGuardian(guardian);
            appendHistoryText(setInteractionHistory, `${guardian.name} has been selected`);
        }
    };

    const translateTo = (lang: string) => {
        const select: any = document.querySelector(".goog-te-combo");
        if (!select) return;
        select.value = lang;
        select.dispatchEvent(new Event("change", {bubbles: true}));
        select.dispatchEvent(new Event("change", {bubbles: true}));
    };

    return (
        <>
            <div id="google_translate_element"></div>

            <Grid container spacing={2} className={styles.rootGrid}>
                <Grid size={{xs: 12, md: 3, lg: 3}}
                      sx={{height: {md: "100%"}, display: {xs: "none", md: "block"}}}>
                    <IncomingMessageBox selectedGuardian={selectedGuardian}/>
                </Grid>

                <Grid size={{xs: 12, md: 6, lg: 6}} className={styles.centerActionBoxContainer}>
                    <Box ref={centerActionBoxRef} className={`${styles.centerActionBox} common-title-font-size `}>
                        <Button
                            variant="contained"
                            className={styles.actionButton}
                            onClick={() => setShowStartIntroductionDialog(true)}
                        >
                            INTRODUCTION TUTORIAL
                        </Button>
                        <Button
                            variant="contained"
                            className={styles.actionButton}
                            onClick={() => setShowChangeLanguagePopup(true)}
                        >
                            LANGUAGE
                        </Button>
                    </Box>
                    <Box className={styles.selectedGuardianSliderWrapper} sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: '100%',
                    }}>
                        <GuardianSlider onSelect={handleSelectGuardian}/>

                        {selectedGuardian && (
                            <Box ref={selectedGuardianWrapperRef} className={styles.selectedGuardianWrapper}>
                                <Box className={`MuiMessage ${styles.selectedGuardianMessage}`}>
                                    You have chosen {selectedGuardian.name} as your guardian. Please select
                                    from the top menu what you like to do next!
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>

                <Grid size={3} sx={{height: "100%", display: {xs: "none", md: "block"}}}>
                    <InteractionInterfaceBox history={interactionHistory}/>
                </Grid>
            </Grid>

            {/* Mobile-only: fixed bottom bar + slide-out drawers */}
            <MobileBottomPanel
                selectedGuardian={selectedGuardian}
                history={interactionHistory}
            />

            {/* Introduction dialog */}
            <CommonDialog
                open={showStartIntroductionDialog}
                title="Introduction Tutorial"
                imageSrc={selectedGuardian?.image}
                content={
                    <List>
                        {flows.map((flow) => (
                            <ListItem key={flow.startFromFlowName}>
                                <Button
                                    sx={{width: "100%"}}
                                    variant="contained"
                                    onClick={() => {
                                        setShowStartIntroductionDialog(false);
                                        if (isMobile) {
                                            console.log('mobile flow start: ', flow.startFromFlowName);
                                            startTutorialFlowMobile(navigate,
                                                flow.startFromFlowName,
                                                flow.tutorialStep)
                                        } else {
                                            console.log('laptop flow start: ', flow.startFromFlowName);
                                            startTutorialFlow(
                                                navigate,
                                                flow.startFromFlowName,
                                                flow.tutorialStep
                                            );
                                        }
                                    }}
                                >
                                    {flow.label.toUpperCase()}
                                </Button>
                            </ListItem>
                        ))}
                    </List>
                }
                onClose={() => setShowStartIntroductionDialog(false)}
                showActions={false}
            />

            {/* Language dialog */}
            <CommonDialog
                open={showChangeLanguagePopup}
                title="Choose language"
                imageSrc={selectedGuardian?.image}
                content={
                    <Autocomplete
                        disablePortal={false}
                        options={languages}
                        onChange={(event, value) => {
                            if (value) {
                                translateTo(value.code);
                                setShowChangeLanguagePopup(false);
                            }
                        }}
                        className={'notranslate'}
                        // getOptionLabel={(option: any) => option.label}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <div className={"notranslate"}>
                                    {option.label}
                                </div>
                            </li>
                        )}
                        sx={{
                            width: 260,

                            /* Input box */
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "16px",
                                background: "rgba(255,255,255,1)",
                                backdropFilter: "none!important",
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 700,
                                boxShadow: "0 0 10px rgba(122, 94, 59, 0.35)",

                                "& fieldset": {borderColor: "transparent"},
                                "&:hover fieldset": {borderColor: "#c4c8ab"},
                                "&.Mui-focused fieldset": {
                                    borderColor: "#c4c8ab",
                                    boxShadow: "0 0 12px #7a5e3b",
                                },
                            },

                            "& .MuiInputBase-input": {
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 700,
                                color: "#414335",
                            },

                            "& .MuiInputLabel-root": {
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 700,
                                color: "#414335",
                            },

                            /* Popper (dropdown) */
                            "& .MuiAutocomplete-popper": {
                                zIndex: 99999,
                                backgroundColor: "rgba(255,255,255,1) !important", // hết trong suốt
                                backdropFilter: "none !important",
                            },

                            /* Dropdown paper */
                            "& .MuiAutocomplete-paper": {
                                marginTop: "6px",
                                borderRadius: "20px",
                                backgroundColor: "rgba(255,255,255,1) !important", // hết trong suốt
                                backdropFilter: "none !important",
                                paddingTop: "10px",
                                paddingBottom: "10px",
                                boxShadow: "0 0 16px rgba(122, 94, 59, 0.35)",
                            },

                            /* Option style */
                            "& .MuiAutocomplete-option": {
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 700,
                                fontSize: "1rem",
                                color: "#414335",
                                padding: "10px 16px",
                                margin: "4px 8px",
                                borderRadius: "12px",

                                "&:hover": {
                                    background: "rgba(134,144,100,0.35)",
                                    color: "white",
                                },
                                "&.Mui-focused": {
                                    background: "#869064",
                                    color: "white",
                                },
                                "&[aria-selected='true']": {
                                    background: "#687543",
                                    color: "white",
                                    boxShadow: "0 0 12px #7a5e3b",
                                },
                            },
                        }}
                        renderInput={(params: any) => (
                            <TextField {...params} label="Choose language"/>
                        )}
                    />

                }
                onClose={() => setShowChangeLanguagePopup(false)}
                showActions={false}
                confirmText="Save"
                cancelText="Cancel"
            />
        </>
    );
};
